/**
 * Cross-reference validation for a template that has already passed the zod
 * schema. The Worker runs this on every save and publish; the editor runs it
 * live so the admin sees problems while authoring.
 */
import {
  ID_DETAIL_LABEL, idPrefillFitHint, idPrefillFits, isBuiltInType, isFilledCondition, isTier3, LOCATION_SET_INFO, typesForSide,
  type Block, type Condition, type ContentBlock, type Expr, type FieldDef, type PartySide, type Sentence, type Template,
} from '@saledeed/shared';
import type { Issue } from './issues';

interface Use { inParty: number; outside: number; seller: number; buyer: number; property: number; path: string }

interface Walk {
  template: Template;
  fields: Map<string, FieldDef>;
  ids: Map<string, string>;
  issues: Issue[];
  partySides: Set<PartySide>;
  pluralSides: { side: PartySide; path: string }[];
  /** field id -> where it is used, to spot document fields used only per party */
  uses: Map<string, Use>;
  partyBlocks: { side: PartySide; path: string }[];
  repeatSides: { side: PartySide; path: string }[];
}

const SIDE_WORD: Record<PartySide, string> = { seller: 'seller', buyer: 'buyer', property: 'property' };
const NUMERIC = new Set(['number', 'currency', 'area', 'calculated']);

function err(w: Walk, code: string, path: string, message: string) {
  w.issues.push({ severity: 'error', code, path, message });
}

function nodeId(w: Walk, id: string, path: string) {
  const prior = w.ids.get(id);
  if (prior !== undefined) err(w, 'node.duplicateId', path, `Id "${id}" is also used at ${prior}`);
  else w.ids.set(id, path);
}

function checkFieldRef(w: Walk, field: string, path: string, inParty: PartySide | null, asCondition = false): FieldDef | undefined {
  const def = w.fields.get(field);
  if (!def) {
    err(w, 'ref.unknownField', path, asCondition
      ? `A ★ condition refers to a field that no longer exists. Edit or remove that ★.`
      : `Placeholder refers to a field that does not exist: "${field}"`);
    return undefined;
  }
  if (def.scope === 'party' && !inParty) {
    err(w, 'ref.partyFieldOutsideParty', path, asCondition
      ? `A ★ condition outside the seller/buyer/property blocks uses "${def.label}", which has a different value for each one. Choose a whole-document field, or move that text into the block.`
      : `"${def.label}" has a value for each seller/buyer/property, so it can only be used inside such a block (or a table row repeated for each)`);
  }
  return def;
}

function checkFormula(w: Walk, def: FieldDef, path: string) {
  let depth = 0;
  const walk = (e: Expr, d: number): void => {
    depth = Math.max(depth, d);
    if (d > 30) return;
    if ('num' in e) return;
    if ('field' in e || 'sum' in e) {
      const id = 'field' in e ? e.field : e.sum;
      const ref = w.fields.get(id);
      if (!ref) { err(w, 'formula.unknownField', path, `"${def.label}": the calculation uses a field that no longer exists`); return; }
      if (isTier3(ref.type)) { err(w, 'formula.tier3', path, `"${def.label}": ID numbers cannot be used in a calculation`); return; }
      if (!NUMERIC.has(ref.type)) err(w, 'formula.notNumeric', path, `"${def.label}": "${ref.label}" is not a number, amount or area`);
      if (ref.type === 'area' && !e.unit) err(w, 'formula.areaUnit', path, `"${def.label}": choose the unit "${ref.label}" is counted in`);
      if (ref.type !== 'area' && e.unit) err(w, 'formula.areaUnit', path, `"${def.label}": "${ref.label}" is not an area, so it has no unit`);
      if ('field' in e && ref.scope === 'party' && def.scope !== 'party') {
        err(w, 'formula.partyField', path, `"${def.label}" is for the whole document, so it can only use "${ref.label}" as a total over every item`);
      }
      if ('sum' in e && ref.scope !== 'party') err(w, 'formula.sumOfDocumentField', path, `"${def.label}": "${ref.label}" is one value for the whole document, so it cannot be totalled`);
      return;
    }
    if ('op' in e) { walk(e.a, d + 1); walk(e.b, d + 1); return; }
    walk(e.if.a, d + 1); walk(e.if.b, d + 1); walk(e.then, d + 1); walk(e.else, d + 1);
  };
  if (def.formula) walk(def.formula, 0);
  if (depth > 30) err(w, 'formula.tooDeep', path, `"${def.label}": the calculation is too long`);
}

/** Calculated fields that (directly or through others) use themselves. */
function formulaCycles(w: Walk): string[] {
  const refs = (e: Expr, out: Set<string>) => {
    if ('field' in e) out.add(e.field);
    else if ('sum' in e) out.add(e.sum);
    else if ('op' in e) { refs(e.a, out); refs(e.b, out); }
    else if ('if' in e) { refs(e.if.a, out); refs(e.if.b, out); refs(e.then, out); refs(e.else, out); }
    return out;
  };
  const bad: string[] = [];
  for (const start of w.fields.values()) {
    if (start.type !== 'calculated' || !start.formula) continue;
    const stack = [...refs(start.formula, new Set())];
    const visited = new Set<string>();
    while (stack.length) {
      const id = stack.pop()!;
      if (id === start.id) { bad.push(start.id); break; }
      if (visited.has(id)) continue;
      visited.add(id);
      const f = w.fields.get(id);
      if (f?.type === 'calculated' && f.formula) stack.push(...refs(f.formula, new Set()));
    }
  }
  return bad;
}

function checkCondition(w: Walk, cond: Condition, path: string, inParty: PartySide | null) {
  const def = checkFieldRef(w, cond.field, `${path}.when`, inParty, true);
  if (!def || isFilledCondition(cond)) return; // "is filled" works for every type, Tier 3 included
  if (isTier3(def.type)) {
    err(w, 'ref.tier3InCondition', `${path}.when`, `A ★ condition compares "${def.label}" to a value; that is not possible because its value never leaves the executive's device. Use "is filled" instead.`);
  }
  if (def.type === 'enum' && !def.options?.includes(cond.equals)) {
    err(w, 'ref.conditionValue', `${path}.when`, `A ★ condition uses a choice that "${def.label}" no longer has. Edit that ★.`);
  }
}

function checkSentence(w: Walk, s: Sentence, path: string, inParty: PartySide | null) {
  nodeId(w, s.id, path);
  if (s.when) checkCondition(w, s.when, path, inParty);
  s.runs.forEach((run, ri) => {
    const rpath = `${path}.runs[${ri}]`;
    const runs = run.kind === 'fragment' ? run.runs : [run];
    if (run.kind === 'fragment' && run.when) checkCondition(w, run.when, rpath, inParty);
    runs.forEach(r => {
      if (r.kind === 'placeholder') {
        const use = w.uses.get(r.field) ?? { inParty: 0, outside: 0, seller: 0, buyer: 0, property: 0, path: rpath };
        if (inParty) { use.inParty++; use[inParty]++; } else use.outside++;
        w.uses.set(r.field, use);
        const def = checkFieldRef(w, r.field, rpath, inParty);
        const isAmount = def?.type === 'currency' || (def?.type === 'calculated' && def.result === 'currency');
        if (def && r.format === 'words' && !isAmount) {
          err(w, 'format.wordsOnNonCurrency', rpath, `"In words" only applies to amounts; "${def.label}" is not an amount`);
        }
        if (def && r.format && r.format !== 'words' && r.format !== 'default' && def.type !== 'area') {
          err(w, 'format.unitOnNonArea', rpath, `"${def.label}" is not an area, so it cannot be printed in another unit`);
        }
      } else if (r.kind === 'plural') {
        w.pluralSides.push({ side: r.side, path: rpath });
      } else if (r.kind === 'partyRef') {
        // A type with no field chosen uses its name (the first text field in its wording): defaultNameFields.
        const refs = Object.values(r.fields).filter((f): f is string => f !== undefined);
        for (const f of refs) {
          const def = w.fields.get(f);
          if (!def) err(w, 'ref.unknownField', rpath, `Placeholder refers to a field that does not exist: "${f}"`);
          else if (def.scope !== 'party') err(w, 'ref.partyRefNotPartyField', rpath, `"${def.label}" is not a seller/buyer field`);
          else if (isTier3(def.type)) err(w, 'ref.tier3InPartyRef', rpath, `"${def.label}" cannot be listed across parties`);
        }
      }
    });
  });
}

function checkContent(w: Walk, b: ContentBlock, path: string, inParty: PartySide | null) {
  nodeId(w, b.id, path);
  switch (b.type) {
    case 'heading':
      checkSentence(w, b.sentence, `${path}.sentence`, inParty);
      return;
    case 'paragraph':
      b.sentences.forEach((s, i) => checkSentence(w, s, `${path}.sentences[${i}]`, inParty));
      return;
    case 'list':
      b.items.forEach((it, i) => {
        nodeId(w, it.id, `${path}.items[${i}]`);
        it.sentences.forEach((s, j) => checkSentence(w, s, `${path}.items[${i}].sentences[${j}]`, inParty));
      });
      return;
    case 'table': {
      const width = b.rows[0]?.cells.length ?? 0;
      b.rows.forEach((row, ri) => {
        const rpath = `${path}.rows[${ri}]`;
        nodeId(w, row.id, rpath);
        if (row.cells.length !== width) err(w, 'table.ragged', rpath, `Row has ${row.cells.length} cells; the table has ${width} columns`);
        let rowSide = inParty;
        if (row.repeat) {
          if (inParty) err(w, 'table.repeatInBlock', rpath, `A row repeated for each ${SIDE_WORD[row.repeat]} cannot be inside a seller/buyer/property block (the block already repeats)`);
          else if (b.headerRow && ri === 0) err(w, 'table.repeatHeader', rpath, 'The header row cannot repeat');
          else { rowSide = row.repeat; w.repeatSides.push({ side: row.repeat, path: rpath }); }
        }
        row.cells.forEach((cell, ci) => {
          nodeId(w, cell.id, `${rpath}.cells[${ci}]`);
          cell.sentences.forEach((s, si) => checkSentence(w, s, `${rpath}.cells[${ci}].sentences[${si}]`, rowSide));
        });
      });
      return;
    }
  }
}

function checkBlock(w: Walk, b: Block, path: string) {
  switch (b.type) {
    case 'heading':
    case 'paragraph':
    case 'list':
    case 'table':
      checkContent(w, b, path, null);
      return;

    case 'clauseSection':
      nodeId(w, b.id, path);
      b.entries.forEach((e, ei) => {
        const epath = `${path}.entries[${ei}]`;
        nodeId(w, e.id, epath);
        if (e.min > e.max) err(w, 'clause.rule', epath, `"${e.label}": minimum (${e.min}) is more than maximum (${e.max})`);
        if (e.max > e.options.length) {
          err(w, 'clause.rule', epath, `"${e.label}": allows choosing ${e.max} but only has ${e.options.length} option(s)`);
        }
        e.options.forEach((o, oi) => {
          const opath = `${epath}.options[${oi}]`;
          nodeId(w, o.id, opath);
          o.body.forEach((p, pi) => checkContent(w, p, `${opath}.body[${pi}]`, null));
        });
      });
      return;

    case 'partyBlock': {
      nodeId(w, b.id, path);
      w.partySides.add(b.side);
      w.partyBlocks.push({ side: b.side, path });
      const variants = Object.entries(b.variants);
      if (!variants.length) err(w, 'party.noVariants', path, `The ${SIDE_WORD[b.side]} block has no wording for any type`);
      const allowed = new Set(typesForSide(w.template, b.side).map(t => t.id));
      for (const [type, blocks] of variants) {
        if (!allowed.has(type)) {
          err(w, 'party.unknownType', `${path}.variants.${type}`, b.side === 'property'
            ? `The property block has wording for "${type}", which is not a property type`
            : `The ${b.side} block has wording for "${type}", which is not a seller/buyer type`);
        }
        blocks.forEach((cb, i) => checkContent(w, cb, `${path}.variants.${type}[${i}]`, b.side));
      }
      return;
    }
  }
}

/**
 * A field whose label says Aadhaar / PAN / TAN but whose type is ordinary text
 * would be treated as Tier 2 and synced to the server draft. Tier comes from
 * the TYPE, so a mislabelled type is a privacy bug; warn loudly.
 */
const SENSITIVE_LABEL: readonly [RegExp, 'aadhaar' | 'pan' | 'tan'][] = [
  [/aadh?aa?r|uid(ai)?\b|ఆధార్/i, 'aadhaar'],
  [/\bpan\b|పాన్/i, 'pan'],
  [/\btan\b/i, 'tan'],
];

export function looksSensitive(label: string): 'aadhaar' | 'pan' | 'tan' | null {
  for (const [re, type] of SENSITIVE_LABEL) if (re.test(label)) return type;
  return null;
}

export function validateTemplate(template: Template): Issue[] {
  const w: Walk = {
    template, fields: new Map(), ids: new Map(), issues: [], partySides: new Set(), pluralSides: [], uses: new Map(), partyBlocks: [], repeatSides: [],
  };

  (template.partyTypes ?? []).forEach((t, i) => {
    const path = `partyTypes[${i}]`;
    if (isBuiltInType(t.id)) err(w, 'type.builtIn', path, `"${t.label}" uses the id of a built-in type`);
    if ((template.partyTypes ?? []).findIndex(x => x.id === t.id) !== i) err(w, 'type.duplicate', path, `Two types have the id "${t.id}"`);
  });

  template.fields.forEach(f => w.fields.set(f.id, f));
  template.fields.forEach((f, i) => {
    const path = `fields[${i}]`;
    if (f.type === 'area' ? false : f.units) err(w, 'field.units', path, `"${f.label}" has units but is not an area`);
    if (f.type === 'calculated') {
      if (!f.formula) err(w, 'field.formula', path, `"${f.label}" is a calculated field but has no calculation yet. Press Calculation… to build it.`);
      else checkFormula(w, f, path);
    } else if (f.formula || f.result || f.decimals !== undefined) {
      err(w, 'field.formula', path, `"${f.label}" has a calculation but is not a calculated field`);
    }
    if (f.type === 'list' && !f.list) err(w, 'field.list', path, `"${f.label}" is a list field; choose which list and column it comes from`);
    if (f.type !== 'list' && f.list) err(w, 'field.list', path, `"${f.label}" has a list but is not a list field`);
    if (f.type === 'location' && !f.location) err(w, 'field.location', path, `"${f.label}" is a location; choose which level it is (District, Mandal…)`);
    if (f.type !== 'location' && f.location) err(w, 'field.location', path, `"${f.label}" has a location level but is not a location field`);
    if (f.idPrefill && f.idPrefill !== 'none' && !idPrefillFits(f.type, f.idPrefill)) {
      err(w, 'field.idPrefill', path, `"${f.label}": "Fill from the Aadhaar card: ${ID_DETAIL_LABEL[f.idPrefill]}" needs ${idPrefillFitHint(f.idPrefill)}. Choose another setting in the Fields tab.`);
    }
    if (f.location && f.location.level >= LOCATION_SET_INFO[f.location.set].levels.length) {
      err(w, 'field.location', path, `"${f.label}": that level does not exist for ${LOCATION_SET_INFO[f.location.set].label}`);
    }
  });
  // A Mandal needs the District of its own address (same group, same "for each" or once) to narrow it.
  const located = template.fields.filter(f => f.type === 'location' && f.location);
  located.forEach(f => {
    const loc = f.location!;
    for (let level = 0; level < loc.level; level++) {
      const parent = located.find(p => p.location!.set === loc.set && p.location!.group === loc.group && p.location!.level === level);
      const name = LOCATION_SET_INFO[loc.set].levels[level];
      if (!parent) {
        err(w, 'location.missingLevel', `fields[${template.fields.indexOf(f)}]`, `"${f.label}" is chosen after a ${name}, but this address has no ${name} field. Insert the location again with the Location button.`);
        break;
      }
      if (parent.scope === 'document' && f.scope === 'party') continue; // a per-item level may follow a whole-document District
      if (parent.scope !== f.scope) {
        err(w, 'location.scope', `fields[${template.fields.indexOf(f)}]`, `"${f.label}" is for each item but its ${name} is asked once (or the other way round). Keep one address's fields together.`);
        break;
      }
    }
    const twin = located.slice(0, located.indexOf(f)).find(p => p.location!.set === loc.set && p.location!.group === loc.group && p.location!.level === loc.level);
    if (twin) err(w, 'location.duplicate', `fields[${template.fields.indexOf(f)}]`, `"${f.label}" and "${twin.label}" are the same level of one address`);
  });
  for (const id of formulaCycles(w)) {
    err(w, 'formula.cycle', `fields[${template.fields.findIndex(f => f.id === id)}]`, `"${w.fields.get(id)!.label}" uses itself in its calculation (directly or through another calculated field)`);
  }
  w.fields.clear();

  template.fields.forEach((f, i) => {
    const path = `fields[${i}]`;
    if (w.fields.has(f.id)) err(w, 'field.duplicate', path, `Field id "${f.id}" is defined twice`);
    w.fields.set(f.id, f);
    const sensitive = looksSensitive(f.label);
    if (sensitive && !isTier3(f.type) && f.type !== 'enum') {
      w.issues.push({
        severity: 'warning', code: 'field.looksSensitive', path,
        message: `"${f.label}" looks like ${sensitive === 'aadhaar' ? 'an Aadhaar' : `a ${sensitive.toUpperCase()}`} number but its type is "${f.type}". Set the type to ${sensitive.toUpperCase()} so the value stays on the executive's device.`,
      });
    }
    if (f.type === 'enum' && !f.options) err(w, 'field.enumOptions', path, `"${f.label}" is a choice field but has no options`);
    if (f.type !== 'enum' && f.options) err(w, 'field.enumOptions', path, `"${f.label}" has options but is not a choice field`);
  });

  template.blocks.forEach((b, i) => checkBlock(w, b, `blocks[${i}]`));

  for (const [field, use] of w.uses) {
    const def = w.fields.get(field);
    if (def?.scope === 'document' && use.inParty > 0 && use.outside === 0) {
      w.issues.push({
        severity: 'warning', code: 'field.documentFieldInPartyOnly', path: use.path,
        message: `"${def.label}" is used only inside a seller/buyer/property block but is a document field, so every one of them would print the same value, and the executive is asked for it once. Make it a per-item field.`,
        fix: { kind: 'makePartyField', field },
      });
    }
  }

  // A seller block without any buyer block (or the reverse) is almost always a
  // block made with the wrong button; the fill form would ask for the wrong people.
  for (const [side, other] of [['seller', 'buyer'], ['buyer', 'seller']] as const) {
    const has = w.partyBlocks.find(b => b.side === side);
    if (has && !w.partyBlocks.some(b => b.side === other)) {
      w.issues.push({
        severity: 'warning', code: 'party.missingSide', path: has.path,
        message: `There is a ${side} block but no ${other} block, so the executive is never asked for a ${other}. If this block holds the ${other}'s details, switch it to "for each ${other === 'seller' ? 'Seller' : 'Buyer'}".`,
        fix: { kind: 'switchSide', side: other },
      });
    }
  }

  // A row repeated for a side the template has no block for still works (the form asks
  // for items), but a property row with no property block is worth a check.
  for (const r of w.repeatSides) w.partySides.add(r.side);

  // Two blocks on one side usually means a buyer block was made with the Seller button.
  for (const side of ['seller', 'buyer'] as const) {
    const blocks = w.partyBlocks.filter(b => b.side === side);
    if (blocks.length > 1) {
      w.issues.push({
        severity: 'warning', code: 'party.multipleBlocks', path: blocks[1]!.path,
        message: `There are ${blocks.length} ${side} blocks, and each repeats for every ${side}. If one of them holds the ${side === 'seller' ? 'buyer' : 'seller'}'s details, switch it to a ${side === 'seller' ? 'Buyer' : 'Seller'} block.`,
        // When the other side has no block at all, the "missing side" warning already
        // offers to switch the FIRST block (the usual mistake); a second button here
        // would switch the real buyer block instead.
        ...(w.partyBlocks.some(b => b.side === (side === 'seller' ? 'buyer' : 'seller'))
          ? { fix: { kind: 'switchSide' as const, side: side === 'seller' ? 'buyer' as const : 'seller' as const } }
          : {}),
      });
    }
  }

  // A field named "Seller ..." used only inside buyer blocks (or the reverse).
  const SIDE_WORDS = { seller: /\b(seller|vendor|executant)s?\b|విక్రేత/i, buyer: /\b(buyer|purchaser|vendee|claimant)s?\b|కొనుగోలు/i } as const;
  for (const [field, use] of w.uses) {
    const def = w.fields.get(field);
    if (!def || def.scope !== 'party') continue;
    for (const [side, other] of [['seller', 'buyer'], ['buyer', 'seller']] as const) {
      if (use[side] > 0 && use[other] === 0 && SIDE_WORDS[other].test(def.label) && !SIDE_WORDS[side].test(def.label)) {
        w.issues.push({
          severity: 'warning', code: 'party.sideMismatch', path: use.path,
          message: `"${def.label}" is used only inside the ${side} block. If that block is really for the ${other}, switch it to a ${other === 'seller' ? 'Seller' : 'Buyer'} block.`,
        });
      }
    }
  }

  for (const p of w.pluralSides) {
    if (!w.partySides.has(p.side)) {
      w.issues.push({
        severity: 'warning', code: 'plural.noPartyBlock', path: p.path,
        message: `Singular/plural wording depends on the number of ${p.side}s, but the template has no ${p.side} block`,
      });
    }
  }
  return w.issues;
}
